import { Form, Formik } from 'formik';
import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import { TFunction, useTranslation } from 'next-i18next';
import { clone, set } from 'lodash';
import { Category, Event, DEFAULT_PRE_SERVICES, INSURANCE_ACCEPTANCE } from '@root/src/types';
import { breakpoint, getViewportWidth, useResize } from '@root/src/services/responsiveProvider';
import yup, { setYupLocale } from '@services/translation/translatedYup';
import { selectEventCategory, selectEventDetail } from '@features/eventDetail/slices/eventDetailServerSlice';
import {
  selectEventCategoryPreServicesGroups,
  selectEventCategoryPreServicesGroupsIds,
} from '@features/eventDetail/slices/eventDetailClientSlice';
import GetFormik from '@features/ui-input/formik/GetFormik';
import preServicesInputFactory from '@features/subscriptions/factories/preServicesInputsFactory';
import InputLine from '@features/ui-input/formik/InputLine';
import { generateDefaultValues } from '@features/subscriptions/factories/servicesDefaultValuesFactory';
import { generateSchema } from '@features/subscriptions/factories/servicesValidationSchemaFactory';
import { generatePreServicesSchema } from '@features/subscriptions/factories/preServicesSchemaFactory';
import { generatePreServicesDefaultValues } from '@features/subscriptions/factories/preServicesDefaultValuesFactory';
import { vipDefaultValue, vipSchemaValidator } from '@features/ui-input/components/InputVIP';
import ServicesListTypePreService from '@features/subscriptions/components/ServicesList/ServicesListTypePreService';

interface FormPreServicesProps {
  onSubmit: any;
  getFormikContext: any;
  eventId: number;
  categoryId: number;
}

const buildDefaultServicesProps = (category: Category, event: Event, t: TFunction) => {
  const { preServices } = category;

  // If the event is VIP, then the category is VIP
  const preServicesCopy = clone(preServices);
  if (event.vip) {
    set(preServicesCopy, 'vip', true);
  }

  const inputs = Object.entries(preServicesCopy)
    .filter(([, value]) => value)
    .map(([key]) => preServicesInputFactory(key as DEFAULT_PRE_SERVICES, t, category))
    .filter(inputProps => inputProps);

  // Removes duplicates for instance club and club mandatory, only the mandatory field should be kept
  return inputs.reduce((acc, current) => {
    const indexInAcc = acc.findIndex(accCurrent => accCurrent.name === current.name);
    if (indexInAcc !== -1) {
      acc[indexInAcc] = current.required ? current : acc[indexInAcc];
    } else {
      acc.push(current);
    }
    return acc;
  }, []);
};

const FormPreServices = ({
  onSubmit,
  getFormikContext,
  eventId,
  categoryId,
}: FormPreServicesProps) => {
  const { t } = useTranslation();
  const router = useRouter();
  const [labelWidth, setLabelWidth] = useState(120);

  const servicesGroupsIds = useSelector(selectEventCategoryPreServicesGroupsIds(categoryId));
  const dynamicPreServices = useSelector(selectEventCategoryPreServicesGroups(categoryId));
  const category = useSelector(selectEventCategory(eventId, categoryId));
  const event = useSelector(selectEventDetail(eventId));
  const defaultPreServicesProps = buildDefaultServicesProps(category, event, t);

  const defaultValues = useMemo(() => {
    const servicesDefaultValues = generateDefaultValues(dynamicPreServices);
    const preServicesDefaultValues = generatePreServicesDefaultValues(defaultPreServicesProps);

    return {
      ...servicesDefaultValues,
      ...preServicesDefaultValues,
      // Certificate field will be managed by Formik setFieldValue, not defaultValues
      ...((category.preServices.vip || event.vip) && vipDefaultValue), // TODO 852 this code should be inside generatePreServicesDefaultValues
      ...(category.preServices.insurance && { [INSURANCE_ACCEPTANCE]: false }), // TODO 852 this code should be inside generatePreServicesDefaultValues
    };
  }, [categoryId]); // Remove certificateInfo._id dependency to avoid clearing form on upload

  const schemaValidator = useMemo(() => {
    setYupLocale(router.locale); // Need to use this before generating the schemas
    const servicesSchema = generateSchema(dynamicPreServices, t);
    const preServicesSchema = generatePreServicesSchema(defaultPreServicesProps, t);
    return yup.object().shape({
      ...preServicesSchema,
      ...servicesSchema,
      ...((category.preServices.vip || event.vip) && vipSchemaValidator(t)), // TODO 852 this code should be inside generatePreServicesSchema
      ...(category.preServices.insurance && {
        [INSURANCE_ACCEPTANCE]: yup.boolean().when(DEFAULT_PRE_SERVICES.INSURANCE, { // TODO 852 this code should be inside generatePreServicesSchema
          is: true,
          then: yup.boolean().oneOf([true], t('preServicesInputFactory_insuranceAcceptanceError.message')),
        }),
      }),
    });
  }, [categoryId]);

  useResize(() => {
    const width = getViewportWidth();
    let newWidth = 400;
    if (width <= (breakpoint.desktop - 30) && width > (breakpoint.desktop - 150)) {
      newWidth = 350;
    } else if (width <= (breakpoint.desktop - 150) && width > (breakpoint.desktop - 300)) {
      newWidth = 300;
    } else if (width <= (breakpoint.desktop - 300) && width > (breakpoint.desktop - 450)) {
      newWidth = 250;
    } else if (width <= (breakpoint.desktop - 450) && width > breakpoint.tablet) {
      newWidth = 200;
    } else if (width <= breakpoint.tablet) {
      newWidth = 120; // Default value
    }
    setLabelWidth(newWidth);
  });

  const hasCertificateUpload = defaultPreServicesProps.some(
    props => props.as.name === 'InputCertificateUpload'
  );

  return (
    <div style={{ marginBottom: hasCertificateUpload ? '96px' : '0px' }}>
      <Formik
        initialValues={defaultValues}
        enableReinitialize
        validationSchema={schemaValidator}
        onSubmit={onSubmit}
      >
        {() => (
          <Form>
            <GetFormik getFormikContext={getFormikContext} />
            {defaultPreServicesProps.map(defaultServiceProps => (
              <InputLine
                labelMinWidth={`${labelWidth}px`}
                {...defaultServiceProps}
                categoryID={categoryId}
              />
            ))}
            {servicesGroupsIds.map(serviceGroupId => (
              <ServicesListTypePreService
                key={serviceGroupId}
                serviceGroupId={serviceGroupId}
                eventId={eventId}
                labelMinWidth={`${labelWidth}px`}
              />
            ))}
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default FormPreServices;
